import { reactive } from "vue";
import ProjectService from "../services/projectService.js";

const PAGE_STEP = 30;
const projectService = new ProjectService();

export const DOC_TABS = {
  ALL: "ALL",
  PERSONAL: "PERSONAL",
  SHARED_WITH_ME: "SHARED_WITH_ME",
};
const TAB_KEYS = Object.values(DOC_TABS);

// Singleton-project documents are real, shareable projects with a
// sessionUserRole — same Personal/Shared-with-me split as useProjectsByTab.js.
function matchesTab(doc, tabKey) {
  if (tabKey === DOC_TABS.PERSONAL) return doc.projectUserRole === "OWNER";
  if (tabKey === DOC_TABS.SHARED_WITH_ME) return doc.projectUserRole !== "OWNER";
  return true;
}

// getSingletonProjects has no cursor — each fetch just re-asks for a bigger
// top-N (see useDocs.js), so the shared "master" list is replaced wholesale
// each time rather than appended to. All three tabs filter this one stream.
const masterState = reactive({ docs: [], hasMore: true });
let masterHasLoadedOnce = false;
let requestedCount = PAGE_STEP;
let lastSortBy = "DATE_ADDED";
let lastSortDirection = "DESC";
let masterFetchPromise = null;

const state = reactive(Object.fromEntries(TAB_KEYS.map((key) => [key, {
  docs: [], hasMore: true, isLoading: false, isLoadingMore: false, hasLoadedOnce: false,
}])));

function recomputeAllTabs() {
  for (const key of TAB_KEYS) {
    state[key].docs = masterState.docs.filter((d) => matchesTab(d, key));
  }
}

function fetchMaster() {
  if (masterFetchPromise) return masterFetchPromise;
  masterFetchPromise = (async () => {
    const res = await projectService.getSingletonProjects({
      first: requestedCount, sortBy: lastSortBy, sortDirection: lastSortDirection,
    });
    masterState.docs = res.documents ?? [];
    masterState.hasMore = res.hasNextPage;
    masterHasLoadedOnce = true;
    recomputeAllTabs();
  })().finally(() => { masterFetchPromise = null; });
  return masterFetchPromise;
}

export function useDocsByTab() {
  async function loadFirstPage(tabKey, sortBy = "DATE_ADDED", sortDirection = "DESC") {
    lastSortBy = sortBy;
    lastSortDirection = sortDirection;
    const s = state[tabKey];
    if (s.hasLoadedOnce) return;
    s.hasLoadedOnce = true;
    s.isLoading = true;
    try {
      await loadMore(tabKey);
    } finally {
      s.isLoading = false;
    }
  }

  // Pulls bigger top-N windows from the shared stream until this tab has
  // gained a full page of matching docs, or the stream is dry.
  async function loadMore(tabKey) {
    const s = state[tabKey];
    if (!s.hasMore || s.isLoadingMore) return;
    s.isLoadingMore = true;
    try {
      const targetCount = s.docs.length + PAGE_STEP;
      while (s.docs.length < targetCount && masterState.hasMore) {
        requestedCount += PAGE_STEP;
        await fetchMaster();
      }
    } finally {
      s.hasMore = masterState.hasMore;
      s.isLoadingMore = false;
    }
  }

  // Sort is applied server-side across the whole shared stream, so changing
  // it invalidates every tab's cached data — reset all, then reload the tab
  // currently in view.
  async function resort(tabKey, sortBy, sortDirection) {
    requestedCount = PAGE_STEP;
    masterState.docs = [];
    masterState.hasMore = true;
    for (const key of TAB_KEYS) {
      state[key].docs = [];
      state[key].hasMore = true;
      state[key].hasLoadedOnce = false;
    }
    await loadFirstPage(tabKey, sortBy, sortDirection);
  }

  // Silently re-fetch in place (no loading flash) — used after uploading a
  // new standalone document, so it shows up without a manual reload.
  async function refresh() {
    if (!masterHasLoadedOnce) return;
    await fetchMaster();
  }

  function remove(docId) {
    masterState.docs = masterState.docs.filter((d) => d.id !== docId);
    recomputeAllTabs();
  }

  return { state, loadFirstPage, loadMore, resort, refresh, remove };
}
